/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import { ICookieValue } from '@msdyn365-commerce/core';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { MAILING_FORM_COOKIE } from '../mailing-list-form/mailing-list-form';
import { AgeGateCookieName } from './../age-gate/age-gate';
import { IMailingListPopupModalProps } from './mailing-list-popup-modal.props.autogenerated';

export type popupModalCookie = {
    name: string;
    age: number;
};

interface IPopupModalState {
    _popupOpen: boolean;
}

/**
 *
 * PopupModal component
 * @extends {React.Component<IMailingListPopupModalProps<{}>>}
 */
@observer
class PopupModal extends React.Component<IMailingListPopupModalProps<{}>, IPopupModalState> {
    private _cookie: popupModalCookie;

    @observable private _cookieRecieved: boolean = false;

    private _cookieRecievedPreForm: boolean = false;

    private _delayStarted: boolean = false;
    @observable private _popupReady: boolean = false;

    constructor(props: IMailingListPopupModalProps<{}>) {
        super(props);

        // starts open automatically so that when the popup is ready, it opens propertly
        this.state = { _popupOpen: true };

        this._cookie = {
            name: 'mailingListCookiePopup',
            age: props.config.remindTime
        };

        this._remindLaterClose = this._remindLaterClose.bind(this);
        this._dismissClose = this._dismissClose.bind(this);
        this._closeModal = this._closeModal.bind(this);
        if (this._cookieExist(MAILING_FORM_COOKIE.name)) {
            this._cookieRecievedPreForm = true;
        }
    }

    public componentDidMount(): void {
        this._startCookiePolling();
    }

    public render(): JSX.Element | null {
        const isOver21 = this._cookieExist(AgeGateCookieName);
        if (isOver21) {
            this._startDelay();
        }
        const { user: { isAuthenticated } } = this.props.context.request;
        const { className, closeText, remindMeLaterText, disableForSignIn } = this.props.config;
        let button: JSX.Element;
        if (this._cookieRecieved) {
            button = <a className='popup-modal-close-text' role='button' onClick={this._closeModal}>{closeText}</a>;
        } else {
            button = <a className='popup-modal-close-text' tabIndex={0} role='button' onClick={this._remindLaterClose} onKeyPress={this._remindLaterClose}>{remindMeLaterText}</a>;
        }

        // we add this._cookieRecievedPreForm because that means *this module did not introduce the cookie*
        // this means we want to notify them that the form was successfully made and not just
        // closing down immediately
        if (this._cookieExist(this._cookie.name) || (this._cookieExist(MAILING_FORM_COOKIE.name) && this._cookieRecievedPreForm)) {
            return null;
        }

        if (disableForSignIn && isAuthenticated) { return null; }

        // Also ensure the code is running in the web context. Don't show the modal in the CMS.
        if (process.env.CURRENT_ENVIRONMENT !== 'web') { return null; }

        if (!this._popupReady) { return null; }
        return (
            <Modal
                centered
                modalClassName={`${className}`}
                autoFocus={true}
                fade={true}
                isOpen={this.state._popupOpen}
                horizontalPosition={'center'}
                verticalPosition={'center'}
                zIndex={1000}
                applicationNode={'rsg-root'}
                toggle={this._dismissClose}
            >
                <ModalHeader className='modal-header border-0 pb-0' toggle={this._dismissClose}>
                </ModalHeader>
                <ModalBody className='px-5 pt-0'>
                    {this._renderedModules}
                    {button}
                </ModalBody>
            </Modal>
        );
    }

    private _startCookiePolling(): void {
        const interval = setInterval(
            () => {
                // MUST call document. turns out that the request object never
                // updates its cookie list when a new cookie is added. only
                // updated on each re-render poll
                // tslint:disable-next-line: no-cookies no-typeof-undefined
                if (typeof window !== 'undefined' && window.document.cookie.includes(MAILING_FORM_COOKIE.name) && !this._cookieRecieved) {
                    this._cookieRecieved = true;
                    clearInterval(interval);
                }
            },
            500
        );
    }

    private get _renderedModules(): JSX.Element {
        return (
            <React.Fragment>
                {this.props.slots.modules.map((slot: React.ReactNode, index: number) => (
                    <React.Fragment key={index}>{slot}</React.Fragment>
                ))}
            </React.Fragment>
        );
    }

    private _remindLaterClose(): void {
        this._closeModal();
        this._setCookie(this._cookie.name, this.props.config.remindTime);
    }

    private _dismissClose(): void {
        this._closeModal();
        // if the cookie exists as of this exact moment, then don't
        // set it
        if (!this._getCookiesDirectly(this._cookie.name)) {
            this._setCookie(this._cookie.name, this.props.config.dismissTime);
        }
    }

    private _closeModal(): void {
        this.setState({ _popupOpen: false });
    }

    private _startDelay(): void {
        if (!this._delayStarted) {
            this._delayStarted = true;
            setTimeout(
                () => {
                    this._popupReady = true;
                },
                this.props.config.delay * 1000
            );
        }
    }

    private _getCookiesDirectly(name: string): string {
        const cookies = {};
        // tslint:disable-next-line: no-cookies
        document.cookie.split(';').forEach(cookie => {
            const [curName, value] = cookie.split('=');
            cookies[curName.trim()] = value;
        });
        return cookies[name];
    }

    private _setCookie(name: string, deltaHoursFromToday: number): void {
        const { cookies } = this.props.context.request;
        const now = new Date(Date.now());
        now.setHours(now.getHours() + deltaHoursFromToday);

        cookies.set(name, false, { expires: now, isEssential: true } as {});
    }

    private _cookieExist(name: string): boolean {
        const { cookies } = this.props.context.request;
        // if cookie exists, don't display
        if (
            // unfortunetly the type does not match the class. isEssential does
            // not exist on cookies.get but is required in this function.
            // override the type to add it
            (cookies.get as (name: string, isEssential: boolean) => ICookieValue<string>)(name, true).status === 'FOUND'
        ) {
            return true;
        }

        return false;
    }
}

export default PopupModal;
